import _ from 'lodash';
import {connect} from 'react-redux';

import {PredicateOf} from '../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {ChannelErrorModel} from '../../../../libs/shared/core/src/models/api/channelErrorModel';
import {ChannelModel} from '../../../../libs/shared/core/src/models/api/channelModel';
import {InboxModel} from '../../../../libs/shared/core/src/models/api/inboxModel';
import {isTeammateNamespace} from '../../../../libs/shared/core/src/models/helpers/namespaceHelpers';
import {
  AppParametricSelector,
  createSelector,
  makeMapDispatchToProps,
  makeMapStateToProps,
  wrapInShallowSelector,
} from '../../../../srcCommon/helpers/reselectHelpers';
import {CellsConversationsDragItem} from '../../../helpers/conversationCellHelpers';
import {teammateCanAccessInbox} from '../../../helpers/modelHelpers';
import {findHighestPriorityChannelError} from '../../../helpers/models/channelHelpers';
import {canMoveConversationIdsToInbox} from '../../../helpers/models/conversationHelpers';
import {RefinementOptionModel} from '../../../models/api/refinementOptionModel';
import {PermissionsEnum} from '../../../models/api/roleModel';
import {AppState} from '../../../store/appState';
import {ThunkAction} from '../../../store/appThunk';
import {getHasPermission} from '../../../store/data/access/accessSelectors';
import {getUserParticipatingChannels} from '../../../store/data/channels/channelsSelectors';
import {
  moveToInboxAction,
  MoveToInboxSourceTypesEnum,
} from '../../../store/data/conversations/moveConversationsActions';
import {getConversations} from '../../../store/data/dataSelectors';
import {toggleDismissChannelErrorsAction} from '../../../store/data/localPreferences/localPreferencesActions';
import {getFindTeammate, getUserId} from '../../../store/data/teammates/teammatesSelectors';
import {InboxUnreachableState} from './workspaceInboxItem';
import {WorkspaceInboxItemContainerBase} from './workspaceInboxItemContainerBase';

/*
 * Props.
 */

interface WorkspaceInboxItemContainerProps {
  inbox: InboxModel;
  selectedRefinement: RefinementOptionModel | undefined;
}

/*
 * Selectors.
 */

function getInbox(_state: AppState, props: WorkspaceInboxItemContainerProps) {
  return props.inbox;
}

function getShouldShowRefinedResultsIndicator(_state: AppState, props: WorkspaceInboxItemContainerProps) {
  return props.selectedRefinement !== undefined;
}

function makeGetInboxChannels() {
  return createSelector([getInbox, getUserParticipatingChannels], (inbox, channels) =>
    channels.filter((c) => c.inboxId === inbox.id),
  );
}

function makeGetChannelIds<P>(
  getChannels: AppParametricSelector<P, ReadonlyArray<ChannelModel>>,
): AppParametricSelector<P, ReadonlyArray<number>> {
  return createSelector([getChannels], (channels) => channels.map((channel) => channel.id));
}

function makeGetHighestPriorityChannelError<P>(
  getChannels: AppParametricSelector<P, ReadonlyArray<ChannelModel>>,
): AppParametricSelector<P, ChannelErrorModel | undefined> {
  return createSelector([getChannels], (channels) => findHighestPriorityChannelError(channels));
}

/*
 * Actions.
 */

function findInboxUnreachableStateAction(
  conversationIds: ReadonlyArray<number>,
  inbox: InboxModel,
): ThunkAction<InboxUnreachableState> {
  return (_dispatch, getState) => {
    const state = getState();
    const conversations = getConversations(state);
    const findTeammate = getFindTeammate(state);
    const userId = getUserId(state);
    const trackerTeammates = _(conversationIds)
      .map((id) => conversations[id])
      .compact()
      .flatMap((c) => _(c.trackers).keys().value())
      .uniq()
      .map((id) => findTeammate(Number(id)))
      .compact()
      .uniqBy((t) => t.id)
      .value();
    const isInboxUnreachable = _(trackerTeammates).some(
      (teammate) => !teammateCanAccessInbox(teammate, inbox),
    );
    const isUserOnlyFollower = trackerTeammates.length === 1 && trackerTeammates[0].id === userId;

    return {isInboxUnreachable, isUserOnlyFollower};
  };
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<WorkspaceInboxItemContainerProps>()(() => {
  const getChannels = makeGetInboxChannels();

  return {
    channelIds: wrapInShallowSelector(makeGetChannelIds(getChannels)),
    channelError: makeGetHighestPriorityChannelError(getChannels),
    shouldShowRefinedResultsIndicator: getShouldShowRefinedResultsIndicator,
    hasPermission: getHasPermission,
  };
});

const mapDispatchToProps = makeMapDispatchToProps()(() => ({
  toggleDismissChannelErrorsAction,
  moveToInboxAction,
  findInboxUnreachableStateAction,
}));

export const WorkspaceInboxItemContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
  (stateProps, dispatchProps, ownProps) => {
    const {inbox} = ownProps;
    const {channelIds, channelError, hasPermission} = stateProps;

    const onShowInboxErrorsClick = () => {
      dispatchProps.toggleDismissChannelErrorsAction(channelIds, false);
    };

    // If this is a teammate inbox, no conversation Drag&Drop support.
    if (isTeammateNamespace(inbox.namespace)) {
      return {
        ...ownProps,
        channelError,
        onShowInboxErrorsClick,
      };
    }

    // Otherwise, implement the conversation Drag&Drop methods.
    const canConversationsDrop: PredicateOf<CellsConversationsDragItem> = (options) => {
      const canConversationsMove = canMoveConversationIdsToInbox(
        options.conversationIds,
        inbox,
        options.findConversationNamespace,
      );

      const userHasPermission = options.conversationIds.every((conversationID) => {
        const conversationNamespace = options.findConversationNamespace(conversationID);
        if (!conversationNamespace) {
          return true;
        }
        return hasPermission(conversationNamespace, PermissionsEnum.CONVERSATION_MOVE);
      });

      return canConversationsMove && userHasPermission;
    };

    const onConversationsDrop = (options: CellsConversationsDragItem, retainTrackerAccess?: boolean) => {
      const {cellsContext, conversationIds} = options;
      dispatchProps.moveToInboxAction(
        cellsContext.teammateId,
        conversationIds,
        inbox.id,
        {
          type: MoveToInboxSourceTypesEnum.WORKSPACE_INBOX_DROP,
        },
        retainTrackerAccess,
      );
    };

    return {
      ...ownProps,
      ...stateProps,
      channelError,
      onShowInboxErrorsClick,
      canConversationsDrop,
      onConversationsDrop,
      findInboxUnreachableState: dispatchProps.findInboxUnreachableStateAction,
    };
  },
)(WorkspaceInboxItemContainerBase);
